import {
    Container,
    ContainerPadding,
    CTA,
    Layout,
    LayoutRowGap,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {
    openCouponLayer,
    setCouponLayerHandle,
} from './layers/launchers/CouponRequestLayerHandle'
import {useFeatureServices} from '../use-feature-services'
import {setAlertSystemLayerHandle} from './layers/launchers/AlertSystemLayerHandle'

import {
    setFormCycleID,
    setPrivacyMarketingVgiVersion,
    setPrivacyProfileVgiVersion,
    setSelectedModelDisplayName,
    setSelectedModelSlug,
} from '../features/userData/userDataSlice'
import {useDispatch, useSelector} from 'react-redux'
import {AppDispatch} from './thunk'
import {usePromotionData} from './usePromotionData'
import {setDisclaimerLayerHandle} from './layers/launchers/DisclaimerLayerHandle'
import {
    setBackendUrl,
    setBrand,
    setCampaignCode,
    setCampaignId,
    setLayout,
    setLoading,
    setPriceList,
    setPriceListData,
} from '../features/general/generalSlice'
import {Loading} from './components/loading'
import {RootState} from './rootReducer'
import isEmpty from 'lodash.isempty'
import {authServiceHelpers} from '../shared/authServiceHelper'
import {
    openRecoverCouponLayer,
    setRecoverCouponLayerHandle,
} from './layers/launchers/RecoverCouponRequestLayerHandle'
import {generateRandomString, getVersionKey} from '../shared/utilities'
import {setTrackingServiceManager} from '../shared/trackingServiceHelper'
import {Global} from '../shared/variables'
import {DealerView} from './dealer/DealerView'
import {TeaserView} from './teaser/TeaserView'

interface PropsApp {
    featureAppId: string
    backendUrl: string
    campaignCode: string
    privacyProfileVgiVersion: string
    privacyMarketingVgiVersion: string
    priceList: string
    layout: string
    brand: string
}

const COUPON_LAYER_ID = 'coupon-request-layer'
const RECOVER_COUPON_LAYER_ID = 'recover-coupon-layer'

// tslint:disable-next-line:cyclomatic-complexity
export function App(props: PropsApp): React.JSX.Element {
    const {t, i18n} = useTranslation('fa-coupon')
    const dispatch = useDispatch() as AppDispatch

    const logged = useSelector((state: RootState) => state.general.logged)
    const configurationPromo = useSelector(
        (state: RootState) => state.configurationPromo.configuration
    )
    const mainCta = useSelector((state: RootState) =>
        !isEmpty(state.configurationPromo.configuration) &&
        state.general.layout !== Global.TEASER_LAYOUT
            ? state.configurationPromo.configuration.cta_opening[
                  getVersionKey()
              ]
            : ''
    )

    const RecoverCTAContainer = styled.div`
        padding-bottom: ${({theme}) => theme.size.static100};
    `

    const AppContainer = styled.div``

    const {'layer-manager': layerManager} = useFeatureServices() as any
    const {'vw:authService': provider} = useFeatureServices() as any
    const {'vw:authservice-config': authserviceConfig} =
        useFeatureServices() as any
    const {'service-config-provider': serviceConfigProvider} =
        useFeatureServices() as any
    const {tracking: trackingManager} = useFeatureServices() as any
    const {'mydealer-service': myDealerService} = useFeatureServices() as any
    const {'ipc-service': ipcService}: any = useFeatureServices() as any

    const authService = provider?.register('market')

    if (typeof props.backendUrl !== 'undefined') {
        dispatch(setBackendUrl(props.backendUrl))
    }

    if (typeof props.privacyProfileVgiVersion !== 'undefined') {
        dispatch(setPrivacyProfileVgiVersion(props.privacyProfileVgiVersion))
    }

    if (typeof props.campaignCode !== 'undefined') {
        dispatch(setCampaignCode(props.campaignCode))
    }

    if (typeof props.priceList !== 'undefined') {
        dispatch(setPriceList(props.priceList))
    }

    if (typeof props.layout !== 'undefined') {
        dispatch(setLayout(props.layout))
    }

    if (typeof props.privacyMarketingVgiVersion !== 'undefined') {
        dispatch(
            setPrivacyMarketingVgiVersion(props.privacyMarketingVgiVersion)
        )
    }
    if (typeof props.brand !== 'undefined') {
        dispatch(setBrand(props.brand))
    }

    useEffect(() => {
        setCouponLayerHandle(layerManager)
        setAlertSystemLayerHandle(layerManager)
        setDisclaimerLayerHandle(layerManager)
        setRecoverCouponLayerHandle(layerManager)

        setTrackingServiceManager(trackingManager)
    }, [])

    useEffect(() => {
        const CAMPAING_ID_KEY = 'vwa_d6_cms.campaignID'

        if (sessionStorage.getItem(CAMPAING_ID_KEY)) {
            dispatch(
                setCampaignId(sessionStorage.getItem(CAMPAING_ID_KEY) || '')
            )
        }
    }, [])

    const loadingConfiguration = usePromotionData(props, myDealerService)

    useEffect(() => {
        i18n.changeLanguage(props.brand)
    }, [i18n, props.brand])

    useEffect(() => {
        if (logged === undefined) {
            dispatch(setLoading(true))
        }
        if (authService) {
            authServiceHelpers(
                authService,
                authserviceConfig,
                serviceConfigProvider
            )
        }
    }, [logged])

    const dynamicOpenCouponLayer = () => {
        dispatch(setFormCycleID(generateRandomString(Global.CYCLE_ID_LENGTH)))
        dispatch(setSelectedModelDisplayName(''))
        dispatch(setSelectedModelSlug(''))
        openCouponLayer()
    }

    const dynamicOpenRecoverLayer = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        e.preventDefault()
        dispatch(setFormCycleID(generateRandomString(Global.CYCLE_ID_LENGTH)))
        openRecoverCouponLayer()
    }

    if (ipcService) {
        const setPriceListDataListener = {
            id: 'onChangePriceListData',
            receive: () => {
                const priceListData = ipcService.get(
                    'fs-coupon',
                    'priceListData'
                )
                if (priceListData) {
                    dispatch(setPriceListData(priceListData))
                    openCouponLayer()
                }
            },
        }
        const recoverFormLayerListener = {
            id: 'onOpenRecoverFormLayer',
            receive: () => {
                openRecoverCouponLayer()
            },
        }

        ipcService.subscribe('setPriceListData', setPriceListDataListener)
        ipcService.subscribe('recoverFormLayer', recoverFormLayerListener)
    }

    return (
        <AppContainer>
            <Container
                padding={{
                    left: ContainerPadding.grid002,
                    right: ContainerPadding.grid002,
                }}
                wrap="always"
            >
                {!loadingConfiguration ? (
                    <>
                        {!props.priceList ? (
                            <>
                                {props.layout === Global.DEALER_LAYOUT ? (
                                    <DealerView />
                                ) : props.layout === Global.TEASER_LAYOUT ? (
                                    <TeaserView />
                                ) : (
                                    <Layout
                                        rowGap={LayoutRowGap.static300}
                                        allowOverflowingContent
                                    >
                                        {!configurationPromo.cta_opening
                                            .hide_cta ? (
                                            <CTA
                                                tag="button"
                                                emphasis="primary"
                                                onClick={dynamicOpenCouponLayer}
                                                aria-controls={COUPON_LAYER_ID}
                                                aria-haspopup="dialog"
                                                aria-label={
                                                    mainCta
                                                        ? mainCta
                                                        : undefined
                                                }
                                            >
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: mainCta
                                                            ? mainCta
                                                            : undefined,
                                                    }}
                                                />
                                            </CTA>
                                        ) : undefined}

                                        {configurationPromo.coupon
                                            .recoverable ? (
                                            <RecoverCTAContainer>
                                                <CTA
                                                    tag="button"
                                                    emphasis="tertiary"
                                                    onClick={(e) =>
                                                        dynamicOpenRecoverLayer(
                                                            e
                                                        )
                                                    }
                                                    aria-controls={
                                                        RECOVER_COUPON_LAYER_ID
                                                    }
                                                    aria-haspopup="dialog"
                                                    aria-label={
                                                        configurationPromo
                                                            .cta_opening
                                                            .coupon_recovery ||
                                                        t('cta.recover')
                                                    }
                                                >
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                configurationPromo
                                                                    .cta_opening
                                                                    .coupon_recovery ||
                                                                t(
                                                                    'cta.recover'
                                                                ),
                                                        }}
                                                    />
                                                </CTA>
                                            </RecoverCTAContainer>
                                        ) : undefined}
                                    </Layout>
                                )}
                            </>
                        ) : undefined}
                    </>
                ) : (
                    <Loading />
                )}
            </Container>
        </AppContainer>
    )
}
